import React, { useMemo } from 'react';

import { SidePage } from '@gogame/components';
import { useChildComponent } from '@gogame/hooks-ts';

import {
  ConceptComments,
  ConceptCommentsProvider,
} from 'scenes/Gallery/GalleryPage/Concepts/Comments';
import { useCommentsCount } from 'scenes/Gallery/GalleryPage/Concepts/Comments/data';
import ConceptPerformance from 'scenes/Gallery/GalleryPage/Concepts/ConceptPerformance';
import {
  useConceptDetails,
  useIsUpdating,
} from 'scenes/Gallery/GalleryPage/Concepts/data';
import {
  TAB_DETAILS,
  TAB_COMMENTS,
  TAB_PERFORMANCE,
  TAB_ACTIONS_LOG,
  TAB_AI_TAGS,
} from 'scenes/Gallery/GalleryPage/constants';
import { useGalleryPermissions } from 'scenes/Gallery/GalleryPage/hooks';
import * as CONCEPT from 'scenes/Gallery/GalleryPage/Concepts/view-constants';

import ConceptDetails from './ConceptDetails';
import ActionsLogTab from './components/ActionLogsTab';
import AttachmentsTab from './components/AttachmentsTab';
import AITagsTab from './components/AITagsTab';

const ConceptDetailsSidePage = ({
  id,
  activeTabId = TAB_DETAILS,
  onClose,
  onExited,
  open,
}) => {
  const [childComponent, openChildComponent] = useChildComponent();
  const { isAllowPerformance, isAllowAttachments } = useGalleryPermissions();
  const { data, isLoading, isRefetching } = useConceptDetails({ id });
  const commentCount = useCommentsCount(id);
  const isUpdating = useIsUpdating();

  const aiTagsCount = useMemo(() => {
    return data?.[CONCEPT.AI_TAGS]?.length || '0';
  }, [data?.[CONCEPT.AI_TAGS]]);

  const handleRemove = () => {
    onClose();
  };

  return (
    <SidePage
      backdrop={false}
      onClose={onClose}
      onExited={onExited}
      loading={isLoading || isUpdating}
      fetching={isRefetching}
      open={open}
      size={'lg'}
    >
      <SidePage.Title
        title={'Concept'}
        subTitle={data?.name}
        entityId={id}
      />
      <SidePage.Tabs activeTabId={activeTabId}>
        <SidePage.Tab label={TAB_DETAILS} data-cy-label={'Details'}>
          {!!data && (
            <ConceptDetails
              data={data}
              onRemove={handleRemove}
              openChildComponent={openChildComponent}
            />
          )}
        </SidePage.Tab>
        {isAllowAttachments ? <AttachmentsTab conceptId={id} /> : null}
        <SidePage.Tab
          label={TAB_AI_TAGS}
          data-cy-label={'AI Tags'}
          count={aiTagsCount}
        >
          <AITagsTab data={data?.[CONCEPT.AI_TAGS]} />
        </SidePage.Tab>
        <SidePage.Tab
          label={TAB_COMMENTS}
          data-cy-label={'Comments'}
          count={commentCount || '0'}
        >
          <SidePage.Content disableGutters={true}>
            <ConceptComments itemSize={12} />
          </SidePage.Content>
        </SidePage.Tab>
        {isAllowPerformance && (
          <SidePage.Tab label={TAB_PERFORMANCE} data-cy-label={'Performance'}>
            <ConceptPerformance conceptId={id} columns={4} />
          </SidePage.Tab>
        )}
        <SidePage.Tab label={TAB_ACTIONS_LOG} data-cy-label={'Usage Logs'}>
          <ActionsLogTab
            conceptId={id}
            openChildComponent={openChildComponent}
          />
        </SidePage.Tab>
      </SidePage.Tabs>
      <SidePage.Actions>
        <SidePage.CloseButton />
      </SidePage.Actions>
      {childComponent}
    </SidePage>
  );
};

export default (props) => {
  return (
    <ConceptCommentsProvider conceptId={props.id}>
      <ConceptDetailsSidePage {...props} />
    </ConceptCommentsProvider>
  );
};

export { default as ConceptDetailsGeneral } from './ConceptDetailsGeneral';
export { default as ConceptDetailsInfo } from './ConceptDetailsInfo';
